import React, { useCallback } from 'react';

// Types
import { GoalItem, User } from 'Shared/constants/types';

// Hooks
import { useGoal } from 'Shared/utils/hooks/use-goal';
import { useTranslation } from 'Shared/utils/hooks/use-translation';

// Components
import WpAvatar from 'Wpds/components/avatar/WpAvatar';
import { WpButton } from 'Wpds/components/button';
import { WpIcon } from 'Wpds/components/icon';
import WpUserInput from 'Wpds/components/user-input/WpUserInput';

// Styles
import './GoalOwner.scss';

type Props = {
  goal: GoalItem;
  showName?: boolean;
  showAvatar?: boolean;
  allowLinkToProfile?: boolean;
  editable?: boolean;
};

const GoalOwner = ({ goal, showAvatar = true, showName = true, editable = false }: Props) => {
  const { tr } = useTranslation();
  const { owner, canChangeStatus, isComplete, isSaving, workspace, handleUpdateGoal } = useGoal({ slug: goal.slug });

  const handleSetOwner = useCallback((user: User) => {
    if (user.id !== goal.assigneeId) handleUpdateGoal({ assigneeId: user.id });
  }, []);

  if (!owner) return null;

  const assigneeName = owner.displayName;
  const baseClassName = 'GoalOwner';

  const CustomInnerDropdown = () => (
    <WpButton variant="tertiary" square slimPadding loading={isSaving}>
      <div className={`${baseClassName}__custom-inner__owner`}>
        <div className={`${baseClassName}__custom-inner__owner__avatar`}>
          <WpAvatar avatar={owner.avatar} size="sm" />
        </div>
        <span className={`${baseClassName}__custom-inner__owner__name`}>{assigneeName}</span>
      </div>
      <WpIcon name="angle-down" />
    </WpButton>
  );

  if (canChangeStatus && editable && !isComplete && !workspace?.isPersonal) {
    return (
      <WpUserInput
        label={tr('perform.goals.owner.goal_owner_label')}
        id="goal-owner-input"
        users={workspace.users}
        // @ts-ignore
        value={owner.id}
        labelHidden
        components={{ innerDropdown: CustomInnerDropdown }}
        onChange={handleSetOwner}
      />
    );
  }

  return (
    <div className={baseClassName}>
      <div className={`${baseClassName}__content`}>
        {showAvatar && <WpAvatar avatar={owner.avatar} size="sm" />}
        {showName && <span className={`${baseClassName}__name`}>{assigneeName}</span>}
      </div>
    </div>
  );
};

export default GoalOwner;
